import { type FormEvent, type ReactElement, useEffect, useState } from 'react';
import { type EmailIntegrationView, type SmsIntegrationView, type WhatsappIntegrationView } from '@vello/api-client';
import {
  type EmailIntegrationForm,
  type EmailFormField,
  type EmailFormIssue,
  type WhatsappIntegrationForm,
  type WhatsappFormField,
  type SmsIntegrationForm,
  type SmsFormField,
  emptyEmailForm,
  emailFormFromView,
  normalizeEmailForm,
  getEmailIntegration,
  setEmailIntegration,
  sendTestEmail,
  emptyWhatsappForm,
  whatsappFormFromView,
  normalizeWhatsappForm,
  getWhatsappIntegration,
  setWhatsappIntegration,
  sendTestWhatsapp,
  emptySmsForm,
  smsFormFromView,
  normalizeSmsForm,
  getSmsIntegration,
  setSmsIntegration,
  sendTestSms,
  isTestPhone,
} from '../../src/index';
import { useAdmin } from '../session';
import { useRouter } from '../router';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { Badge, Button, Field, Notice, PageHeader, Panel, Select, TextInput, FormRow, SectionTitle, FormGrid, Cluster, Stack, Text, FieldCell } from '@vello/web-theme';
import { JsonArea, OTP_ISSUE_KEY, SmsGatewayFields } from '../components/otp-slot-fields';
import { GeoProvidersPanel } from '../components/geo-providers-panel';
import { AiProvidersPanel } from '../components/ai-providers-panel';
import { MapTilesPanel } from '../components/map-tiles-panel';
import { WalletTopUpPanel } from '../components/wallet-topup-panel';
import { StoragePanel } from '../components/storage-panel';
import { PushPanel } from '../components/push-panel';

const ISSUE_KEY: Readonly<Record<EmailFormIssue['code'], string>> = {
  INVALID_EMAIL: 'int.err.email',
};

/** The guided setup entry point (D343) shown in the WhatsApp and SMS panels. */
function WizardEntry(): ReactElement {
  const { navigate } = useRouter();
  const { t } = useT();
  return (
    <Cluster gap={3} wrap>
      <Button variant="secondary" onClick={() => navigate('integrations_otp_setup')}>
        {t('int.otp.wizard')}
      </Button>
      <FieldCell size="lg"><Text role="caption" tone="tertiary">{t('int.otp.wizard.hint')}</Text></FieldCell>
    </Cluster>
  );
}
/**
 * Integrations — provider credential vault (ADR-0037; D305). The operator configures the email provider (from
 * address, display name, reply-to, enabled) and pastes/rotates the API key, which is sealed into the
 * KEK-encrypted vault server-side. The key is **write-only**: this screen only ever shows a masked hint + a
 * configured badge, never the key. A "send test email" verifies the whole path end to end.
 */
export function IntegrationsScreen(): ReactElement {
  const { runtime, me } = useAdmin();
  const { t } = useT();
  const { fail } = useVocab();

  const [view, setView] = useState<EmailIntegrationView | undefined>(undefined);
  const [form, setForm] = useState<EmailIntegrationForm>(() => emptyEmailForm());
  const [errors, setErrors] = useState<Partial<Record<EmailFormField, string>>>({});
  const [loadMsg, setLoadMsg] = useState<string | undefined>(undefined);
  const [saveMsg, setSaveMsg] = useState<{ tone: 'success' | 'danger'; text: string } | undefined>(undefined);
  const [saveBusy, setSaveBusy] = useState(false);
  // D854 (L2-014): every provider card saves on its own, so no Save is the screen's primary action: all of them are secondary.

  const [testTo, setTestTo] = useState('');
  const [testMsg, setTestMsg] = useState<{ tone: 'success' | 'danger'; text: string } | undefined>(undefined);
  const [testBusy, setTestBusy] = useState(false);

  const set = (field: keyof EmailIntegrationForm) => (v: string | boolean): void => setForm((f) => ({ ...f, [field]: v }));

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const res = await getEmailIntegration(runtime);
      if (cancelled) return;
      if (res.ok) {
        setView(res.value);
        setForm(emailFormFromView(res.value));
      } else {
        setLoadMsg(t('int.load.failed', { code: fail(res.error) }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [runtime, t]);

  const onSave = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setSaveMsg(undefined);
    const normalized = normalizeEmailForm(form);
    if (!normalized.ok) {
      const next: Partial<Record<EmailFormField, string>> = {};
      for (const issue of normalized.error) next[issue.field] = t(ISSUE_KEY[issue.code]);
      setErrors(next);
      return;
    }
    setErrors({});
    setSaveBusy(true);
    const res = await setEmailIntegration(runtime, normalized.value);
    setSaveBusy(false);
    if (res.ok) {
      setView(res.value);
      setForm(emailFormFromView(res.value));
      setSaveMsg({ tone: 'success', text: t('int.saved') });
    } else {
      setSaveMsg({ tone: 'danger', text: t('int.save.failed', { code: fail(res.error) }) });
    }
  };

  const onTest = async (): Promise<void> => {
    setTestMsg(undefined);
    setTestBusy(true);
    const res = await sendTestEmail(runtime, testTo);
    setTestBusy(false);
    if (res.ok) {
      setTestMsg({ tone: 'success', text: t('int.test.sent', { to: res.value.to }) });
    } else {
      const text = res.error.code === 'CONFLICT' ? t('int.test.notConfigured') : t('int.test.failed', { code: fail(res.error) });
      setTestMsg({ tone: 'danger', text });
    }
  };

  const configured = view?.configured === true;
  const keyHint = view?.maskedHint;

  return (
    <>
      <PageHeader title={t('int.title')} subtitle={t('int.subtitle')} />

      <div>
        <Panel>
          <Stack gap={4}>
            <Cluster gap={3} align="baseline" wrap>
              <Text as="h2" role="title">{t('int.email.heading')}</Text>
              <Badge tone={configured ? 'success' : 'warning'}>{configured ? t('int.status.configured') : t('int.status.notConfigured')}</Badge>
              {view?.config.enabled === true && <Badge tone="success">{t('int.status.enabled')}</Badge>}
            </Cluster>
            <Text as="p" tone="secondary">{t('int.email.hint')}</Text>
            {loadMsg !== undefined && (
              <Notice tone="danger">{loadMsg}</Notice>
            )}
            <form onSubmit={(e) => void onSave(e)} noValidate>
              <Stack gap={4}>
                <FormGrid>
                  <Field label={t('int.field.fromEmail')} htmlFor="int-from-email" hint={errors.fromEmail ?? t('int.field.fromEmail.hint')}>
                    <TextInput id="int-from-email" type="email" value={form.fromEmail} onChange={(e) => set('fromEmail')(e.target.value)} placeholder="no-reply@usevello.app" required />
                  </Field>
                  <Field label={t('int.field.fromName')} htmlFor="int-from-name" hint={t('int.field.fromName.hint')}>
                    <TextInput id="int-from-name" value={form.fromName} onChange={(e) => set('fromName')(e.target.value)} placeholder="Vello" />
                  </Field>
                  <Field label={t('int.field.replyTo')} htmlFor="int-reply-to" hint={errors.replyTo ?? t('int.field.replyTo.hint')}>
                    <TextInput id="int-reply-to" type="email" value={form.replyTo} onChange={(e) => set('replyTo')(e.target.value)} placeholder="support@usevello.app" />
                  </Field>
                  <Field label={t('int.field.enabled')} htmlFor="int-enabled" hint={t('int.field.enabled.hint')}>
                    <Select id="int-enabled" value={form.enabled ? 'on' : 'off'} onChange={(e) => set('enabled')(e.target.value === 'on')}>
                      <option value="on">{t('int.field.enabled.on')}</option>
                      <option value="off">{t('int.field.enabled.off')}</option>
                    </Select>
                  </Field>
                  <Field label={t('int.field.apiKey')} htmlFor="int-api-key" hint={configured ? t('int.field.apiKey.hintConfigured', { hint: keyHint ?? '' }) : t('int.field.apiKey.hint')}>
                    <TextInput id="int-api-key" type="password" autoComplete="off" value={form.apiKey} onChange={(e) => set('apiKey')(e.target.value)} placeholder={configured ? '••••••••' : 're_…'} />
                  </Field>
                </FormGrid>
                {saveMsg !== undefined && (
                  <Notice tone={saveMsg.tone}>{saveMsg.text}</Notice>
                )}
                <Button type="submit" variant="secondary" disabled={saveBusy}>
                  {saveBusy ? t('int.save.busy') : t('int.save')}
                </Button>
              </Stack>
            </form>
          </Stack>
        </Panel>
      </div>

      {/* D727: the object store's real bucket (Cloudflare R2; the access key pair write-only) - documents, rider photos, verification images. */}
      <StoragePanel />
      <WhatsappPanel />
      <SmsPanel />
      {/* D854 (L7a-033): the push provider (Firebase Cloud Messaging; the service-account key write-only). */}
      <PushPanel />
      <GeoProvidersPanel />
      {/* D631 (doc 25 section 11): the OpenRouter key and the self-hosted speech endpoints (write-only keys). */}
      <AiProvidersPanel />
      {/* D471 (ADR-0089): the tile source the live map draws (a vault slot, no key, no provider in code). */}
      <MapTilesPanel />
      {/* D459 (ADR-0074): the driver wallet's top-up PSP per country (write-only key + webhook token). */}
      <WalletTopUpPanel />

      <Panel>
        <Stack gap={4}>
          <SectionTitle>{t('int.test.heading')}</SectionTitle>
          <Text as="p" tone="secondary">{t('int.test.hint')}</Text>
          <FormRow columns="minmax(0, 320px)"
            action={
              <Button disabled={testBusy || !configured} onClick={() => void onTest()}>
                {testBusy ? t('int.test.busy') : t('int.test.send')}
              </Button>
            }
          >
            <Field label={t('int.field.to')} htmlFor="int-test-to" hint={me?.email !== undefined && me.email !== '' ? t('int.field.to.hint', { operator: me.email }) : t('int.field.to.hint.noEmail')}>
              <TextInput id="int-test-to" type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="me@usevello.app" />
            </Field>
          </FormRow>
          {testMsg !== undefined && <Notice tone={testMsg.tone}>{testMsg.text}</Notice>}
        </Stack>
      </Panel>
    </>
  );
}

/** The "send a test code" row shared by the WhatsApp and SMS panels: an E.164 phone the operator owns + a locale. */
function TestCodeRow({ idPrefix, configured, onSend }: { idPrefix: string; configured: boolean; onSend: (to: string, locale: string) => Promise<{ ok: true; to: string } | { ok: false; code: string }> }): ReactElement {
  const { t } = useT();
  const [to, setTo] = useState('');
  const [locale, setLocale] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ tone: 'success' | 'danger'; text: string } | undefined>(undefined);
  const run = async (): Promise<void> => {
    setMsg(undefined);
    if (!isTestPhone(to)) {
      setMsg({ tone: 'danger', text: t('int.err.phone') });
      return;
    }
    setBusy(true);
    const res = await onSend(to, locale);
    setBusy(false);
    if (res.ok) setMsg({ tone: 'success', text: t('int.otp.test.sent', { to: res.to }) });
    else setMsg({ tone: 'danger', text: res.code === 'CONFLICT' ? t('int.otp.test.notConfigured') : t('int.test.failed', { code: res.code }) });
  };
  return (
    <div>
      <Text as="h3" role="label">{t('int.otp.test.heading')}</Text>
      <Text as="p" tone="secondary">{t('int.otp.test.hint')}</Text>
      <FormRow columns="minmax(0, 260px) minmax(0, 160px)"
        action={
          <Button disabled={busy || !configured} onClick={() => void run()}>
            {busy ? t('int.test.busy') : t('int.test.send')}
          </Button>
        }
      >
        <Field label={t('int.otp.field.to')} htmlFor={`${idPrefix}-test-to`}>
          <TextInput id={`${idPrefix}-test-to`} type="tel" value={to} onChange={(e) => setTo(e.target.value)} placeholder="+" />
        </Field>
        <Field label={t('int.otp.field.to.locale')} htmlFor={`${idPrefix}-test-locale`}>
          <TextInput id={`${idPrefix}-test-locale`} value={locale} onChange={(e) => setLocale(e.target.value)} placeholder="pt" />
        </Field>
      </FormRow>
      {msg !== undefined && <Notice tone={msg.tone}>{msg.text}</Notice>}
    </div>
  );
}

/** WhatsApp Authentication (Meta Cloud API) — the PRIMARY OTP channel (D320). The access token is write-only. */
function WhatsappPanel(): ReactElement {
  const { runtime } = useAdmin();
  const { t } = useT();
  const { fail } = useVocab();
  const [view, setView] = useState<WhatsappIntegrationView | undefined>(undefined);
  const [form, setForm] = useState<WhatsappIntegrationForm>(() => emptyWhatsappForm());
  const [errors, setErrors] = useState<Partial<Record<WhatsappFormField, string>>>({});
  const [msg, setMsg] = useState<{ tone: 'success' | 'danger'; text: string } | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const set = (field: keyof WhatsappIntegrationForm) => (v: string | boolean): void => setForm((f) => ({ ...f, [field]: v }));

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const res = await getWhatsappIntegration(runtime);
      if (cancelled) return;
      if (res.ok) {
        setView(res.value);
        setForm(whatsappFormFromView(res.value));
      } else {
        setMsg({ tone: 'danger', text: t('int.load.failed', { code: fail(res.error) }) });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [runtime, t]);

  const onSave = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setMsg(undefined);
    const normalized = normalizeWhatsappForm(form);
    if (!normalized.ok) {
      const next: Partial<Record<WhatsappFormField, string>> = {};
      for (const issue of normalized.error) next[issue.field] = t(OTP_ISSUE_KEY[issue.code]);
      setErrors(next);
      return;
    }
    setErrors({});
    setBusy(true);
    const res = await setWhatsappIntegration(runtime, normalized.value);
    setBusy(false);
    if (res.ok) {
      setView(res.value);
      setForm(whatsappFormFromView(res.value));
      setMsg({ tone: 'success', text: t('int.saved') });
    } else {
      setMsg({ tone: 'danger', text: t('int.save.failed', { code: fail(res.error) }) });
    }
  };

  const configured = view?.configured === true;
  return (
    <div>
      <Panel>
        <Stack gap={4}>
          <Cluster gap={3} align="baseline" wrap>
            <Text as="h2" role="title">{t('int.whatsapp.heading')}</Text>
            <Badge tone={configured ? 'success' : 'warning'}>{configured ? t('int.status.configured') : t('int.status.notConfigured')}</Badge>
            {view?.config.enabled === true && <Badge tone="success">{t('int.status.enabled')}</Badge>}
          </Cluster>
          <Text as="p" tone="secondary">{t('int.otp.hint')}</Text>
          <Text as="p" tone="secondary">{t('int.whatsapp.hint')}</Text>
          <WizardEntry />
          <form onSubmit={(e) => void onSave(e)} noValidate>
            <Stack gap={4}>
              <FormGrid>
                <Field label={t('int.whatsapp.field.phoneNumberId')} htmlFor="wa-phone-number-id" hint={errors.phoneNumberId}>
                  <TextInput id="wa-phone-number-id" value={form.phoneNumberId} onChange={(e) => set('phoneNumberId')(e.target.value)} placeholder="1234567890" required />
                </Field>
                <Field label={t('int.whatsapp.field.businessAccountId')} htmlFor="wa-waba-id" hint={errors.businessAccountId}>
                  <TextInput id="wa-waba-id" value={form.businessAccountId} onChange={(e) => set('businessAccountId')(e.target.value)} />
                </Field>
                <Field label={t('int.whatsapp.field.apiVersion')} htmlFor="wa-api-version" hint={errors.apiVersion}>
                  <TextInput id="wa-api-version" value={form.apiVersion} onChange={(e) => set('apiVersion')(e.target.value)} placeholder="v21.0" />
                </Field>
                <Field label={t('int.otp.field.defaultLocale')} htmlFor="wa-default-locale" hint={errors.defaultLocale ?? t('int.otp.field.defaultLocale.hint')}>
                  <TextInput id="wa-default-locale" value={form.defaultLocale} onChange={(e) => set('defaultLocale')(e.target.value)} placeholder="pt" />
                </Field>
                <Field label={t('int.otp.field.cost')} htmlFor="wa-cost-minor" hint={errors.approxCost ?? t('int.otp.field.cost.hint')}>
                  <Cluster gap={2}>
                    <TextInput id="wa-cost-minor" inputMode="numeric" value={form.approxCostMinor} onChange={(e) => set('approxCostMinor')(e.target.value)} placeholder="4" />
                    <TextInput id="wa-cost-currency" value={form.approxCostCurrency} onChange={(e) => set('approxCostCurrency')(e.target.value)} placeholder="USD" className="vello-ab-narrow" />
                  </Cluster>
                </Field>
                <Field label={t('int.field.enabled')} htmlFor="wa-enabled" hint={t('int.otp.field.enabled.hint')}>
                  <Select id="wa-enabled" value={form.enabled ? 'on' : 'off'} onChange={(e) => set('enabled')(e.target.value === 'on')}>
                    <option value="on">{t('int.field.enabled.on')}</option>
                    <option value="off">{t('int.field.enabled.off')}</option>
                  </Select>
                </Field>
                <Field label={t('int.whatsapp.field.accessToken')} htmlFor="wa-access-token" hint={configured ? t('int.field.apiKey.hintConfigured', { hint: view?.maskedHint ?? '' }) : t('int.field.apiKey.hint')}>
                  <TextInput id="wa-access-token" type="password" autoComplete="off" value={form.accessToken} onChange={(e) => set('accessToken')(e.target.value)} placeholder={configured ? '••••••••' : 'EAA…'} />
                </Field>
              </FormGrid>
              <Field label={t('int.whatsapp.field.templates')} htmlFor="wa-templates" hint={errors.templatesJson ?? t('int.whatsapp.field.templates.hint')}>
                <JsonArea id="wa-templates" value={form.templatesJson} onChange={(e) => set('templatesJson')(e.target.value)} />
              </Field>
              {msg !== undefined && (
                <Notice tone={msg.tone}>{msg.text}</Notice>
              )}
              <Button type="submit" variant="secondary" disabled={busy}>
                {busy ? t('int.save.busy') : t('int.save')}
              </Button>
            </Stack>
          </form>
          <TestCodeRow
            idPrefix="wa"
            configured={configured}
            onSend={async (to, locale) => {
              const res = await sendTestWhatsapp(runtime, to, locale);
              return res.ok ? { ok: true, to: res.value.to } : { ok: false, code: res.error.code };
            }}
          />
        </Stack>
      </Panel>
    </div>
  );
}

/** The SMS fallback gateway — any HTTP gateway described by data (D320). The gateway key is write-only. */
function SmsPanel(): ReactElement {
  const { runtime } = useAdmin();
  const { t } = useT();
  const { fail } = useVocab();
  const [view, setView] = useState<SmsIntegrationView | undefined>(undefined);
  const [form, setForm] = useState<SmsIntegrationForm>(() => emptySmsForm());
  const [errors, setErrors] = useState<Partial<Record<SmsFormField, string>>>({});
  const [msg, setMsg] = useState<{ tone: 'success' | 'danger'; text: string } | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const set = (field: keyof SmsIntegrationForm) => (v: string | boolean): void => setForm((f) => ({ ...f, [field]: v }));

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const res = await getSmsIntegration(runtime);
      if (cancelled) return;
      if (res.ok) {
        setView(res.value);
        setForm(smsFormFromView(res.value));
      } else {
        setMsg({ tone: 'danger', text: t('int.load.failed', { code: fail(res.error) }) });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [runtime, t]);

  const onSave = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setMsg(undefined);
    const normalized = normalizeSmsForm(form);
    if (!normalized.ok) {
      const next: Partial<Record<SmsFormField, string>> = {};
      for (const issue of normalized.error) next[issue.field] = t(OTP_ISSUE_KEY[issue.code]);
      setErrors(next);
      return;
    }
    setErrors({});
    setBusy(true);
    const res = await setSmsIntegration(runtime, normalized.value);
    setBusy(false);
    if (res.ok) {
      setView(res.value);
      setForm(smsFormFromView(res.value));
      setMsg({ tone: 'success', text: t('int.saved') });
    } else {
      setMsg({ tone: 'danger', text: t('int.save.failed', { code: fail(res.error) }) });
    }
  };

  const configured = view?.configured === true;
  return (
    <div>
      <Panel>
        <Stack gap={4}>
          <Cluster gap={3} align="baseline" wrap>
            <Text as="h2" role="title">{t('int.sms.heading')}</Text>
            <Badge tone={configured ? 'success' : 'warning'}>{configured ? t('int.status.configured') : t('int.status.notConfigured')}</Badge>
            {view?.config.enabled === true && <Badge tone="success">{t('int.status.enabled')}</Badge>}
          </Cluster>
          <Text as="p" tone="secondary">{t('int.sms.hint')}</Text>
          <WizardEntry />
          <form onSubmit={(e) => void onSave(e)} noValidate>
            <Stack gap={4}>
              <SmsGatewayFields idPrefix="sms" form={form} setForm={setForm} errors={errors} configured={configured} maskedHint={view?.maskedHint} />
              <FormGrid>
                <Field label={t('int.field.enabled')} htmlFor="sms-enabled" hint={t('int.otp.field.enabled.hint')}>
                  <Select id="sms-enabled" value={form.enabled ? 'on' : 'off'} onChange={(e) => set('enabled')(e.target.value === 'on')}>
                    <option value="on">{t('int.field.enabled.on')}</option>
                    <option value="off">{t('int.field.enabled.off')}</option>
                  </Select>
                </Field>
              </FormGrid>
              {msg !== undefined && (
                <Notice tone={msg.tone}>{msg.text}</Notice>
              )}
              <Button type="submit" variant="secondary" disabled={busy}>
                {busy ? t('int.save.busy') : t('int.save')}
              </Button>
            </Stack>
          </form>
          <TestCodeRow
            idPrefix="sms"
            configured={configured}
            onSend={async (to, locale) => {
              const res = await sendTestSms(runtime, to, locale);
              return res.ok ? { ok: true, to: res.value.to } : { ok: false, code: res.error.code };
            }}
          />
        </Stack>
      </Panel>
    </div>
  );
}
